import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import TimeOnlyField from "@/components/TimeOnlyField";
import DateField from "@/components/DateField";
import { todayLocal } from "@/lib/timefmt";

const DOW_LETTERS = ["S", "M", "T", "W", "T", "F", "S"];

export default function DutyTemplatesTab() {
  const { user } = useAuth();
  const isSuper = (user?.role === "admin" && !user?.admin_level) || user?.admin_level === "global";
  const isUnitAdmin = user?.admin_level === "unit";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [roles, setRoles] = useState([]);
  const [units, setUnits] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", role_id: "", unit_id: "" });
  const [anchor, setAnchor] = useState(todayLocal());
  const [numDays, setNumDays] = useState(7);
  const [days, setDays] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkStart, setBulkStart] = useState("08:00");
  const [bulkEnd, setBulkEnd] = useState("17:00");

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      setCompanyId((prev) => prev || (isSuper ? r.data[0]?.id : user?.company_id) || "");
    }).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => {
    if (!companyId) return;
    api.get(`/admin/duty-templates?company_id=${companyId}`).then((r) => setTemplates(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get(`/roles?company_id=${companyId}`).then((r) => setRoles(r.data)).catch(() => {});
    api.get(`/org/tree?company_id=${companyId}`).then((r) => setUnits(r.data.units || [])).catch(() => {});
  };
  useEffect(() => { load(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setDays((prev) => Array.from({ length: numDays }, (_, i) => prev[i] || { duty: true, start: bulkStart, end: bulkEnd }));
  }, [numDays]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setEditingId(null);
    setForm({ name: "", role_id: "", unit_id: "" });
    setAnchor(todayLocal());
    setNumDays(7);
    setDays(Array.from({ length: 7 }, () => ({ duty: true, start: "08:00", end: "17:00" })));
    setSelected(new Set());
    setBulkStart("08:00");
    setBulkEnd("17:00");
  };

  const edit = (t) => {
    setEditingId(t.id);
    setForm({ name: t.name, role_id: t.role_id || "", unit_id: t.unit_id || "" });
    setAnchor(t.anchor_date || todayLocal());
    setBulkStart(t.duty_start || "08:00");
    setBulkEnd(t.duty_end || "17:00");
    const td = (t.days?.length)
      ? t.days.map((d) => ({ duty: !!d.duty, start: d.start || t.duty_start, end: d.end || t.duty_end }))
      : Array.from({ length: 7 }, () => ({ duty: true, start: t.duty_start || "08:00", end: t.duty_end || "17:00" }));
    setNumDays(td.length);
    setDays(td);
    setSelected(new Set());
  };

  const toggleSel = (i) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(i)) n.delete(i); else n.add(i);
    return n;
  });

  const setDuty = (duty) => setDays((prev) => prev.map((d, i) => selected.has(i) ? { ...d, duty, ...(duty ? { start: bulkStart, end: bulkEnd } : {}) } : d));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Enter a template name"); return; }
    const grid = days.length ? days : Array.from({ length: numDays }, () => ({ duty: true, start: bulkStart, end: bulkEnd }));
    if (grid.some((d) => d.duty && (!d.start || !d.end))) { toast.error("All duty days need start and end times"); return; }
    const payload = {
      company_id: companyId, name: form.name.trim(),
      role_id: form.role_id || null, unit_id: form.unit_id || null,
      duty_start: bulkStart, duty_end: bulkEnd,
      anchor_date: anchor, days: grid,
    };
    try {
      if (editingId) {
        await api.put(`/admin/duty-templates/${editingId}`, payload);
        toast.success("Template updated");
      } else {
        await api.post("/admin/duty-templates", payload);
        toast.success("Template created");
      }
      resetForm();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this duty template?")) return;
    await api.delete(`/admin/duty-templates/${id}`);
    if (editingId === id) resetForm();
    load();
  };

  const dayLabel = (i) => {
    const d = new Date(`${anchor}T00:00:00`);
    d.setDate(d.getDate() + i);
    return { dow: DOW_LETTERS[d.getDay()], num: d.getDate(), iso: d.toISOString().slice(0, 10) };
  };

  const roleName = (id) => id ? (roles.find((r) => r.id === id)?.name || "—") : "All roles";
  const unitName = (id) => id ? (units.find((u) => u.id === id)?.name || "—") : "All org units (tree-wide)";

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 md:order-2" data-testid="duty-template-form">
        <div className="flex items-center justify-between mb-1">
          <div className="font-heading text-lg font-semibold">{editingId ? "Edit Duty Template" : "New Duty Template"}</div>
          {editingId && (
            <button onClick={resetForm} data-testid="duty-template-cancel-edit"
              className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors">
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
          )}
        </div>
        <p className="text-[11px] text-slate-500 mb-3">
          Leave role empty to apply to all roles in one org unit; leave unit empty to default tree-wide for the role.
        </p>
        {isSuper && (
          <>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Company</label>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="duty-company-select"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </>
        )}
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Template name</label>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="duty-template-name"
          placeholder="e.g. Civil Pilot Training Day"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3" />
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Role</label>
        <select value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })} data-testid="duty-template-role"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3">
          <option value="">All roles (in the chosen unit)</option>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        {!isUnitAdmin && (
          <>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Org unit</label>
            <select value={form.unit_id} onChange={(e) => setForm({ ...form, unit_id: e.target.value })} data-testid="duty-template-unit"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3">
              <option value="">All org units in my tree</option>
              {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </>
        )}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">First date of pattern</label>
            <DateField value={anchor} onChange={setAnchor} testId="duty-template-anchor" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Pattern days (1–30)</label>
            <input type="number" min={1} max={30} value={numDays}
              onChange={(e) => setNumDays(Math.max(1, Math.min(30, Number(e.target.value) || 1)))}
              data-testid="duty-template-num-days"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" />
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3 p-3 rounded-md border border-slate-800 bg-slate-950/40 mb-3">
          <TimeOnlyField label="Duty start" testId="duty-template-start" value={bulkStart} onChange={setBulkStart} />
          <TimeOnlyField label="Duty end" testId="duty-template-end" value={bulkEnd} onChange={setBulkEnd} />
          <button onClick={() => setDuty(true)} data-testid="duty-template-set-duty"
            className="px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium">Set → Duty</button>
          <button onClick={() => setDuty(false)} data-testid="duty-template-set-free"
            className="px-3 py-2 rounded-md bg-slate-700 hover:bg-slate-600 text-xs font-medium">Set → Free</button>
        </div>
        <div className="text-[11px] text-slate-500 mb-2">{selected.size} selected — click day boxes to select</div>
        <div className="grid grid-cols-7 gap-1.5 mb-3" data-testid="duty-template-grid">
          {days.map((d, i) => {
            const lbl = dayLabel(i);
            const sel = selected.has(i);
            return (
              <button key={lbl.iso} onClick={() => toggleSel(i)} data-testid={`duty-template-day-${i}`}
                className={`rounded-md border p-1.5 text-center transition-colors ${
                  sel ? "ring-2 ring-amber-400 border-amber-400" : ""
                } ${d.duty ? "bg-blue-600 border-blue-400 text-white shadow-sm" : "bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-600"}`}>
                <div className="text-[9px] font-mono-num uppercase tracking-widest opacity-70">{lbl.dow}</div>
                <div className="font-mono-num text-sm font-semibold">{lbl.num}</div>
                <div className={`text-[9px] font-mono-num mt-0.5 ${d.duty ? "text-blue-100" : ""}`}>{d.duty ? `${d.start}-${d.end}` : "FREE"}</div>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-slate-500 mb-3">The pattern repeats from the first date, cycling through the grid.</p>
        <button onClick={save} data-testid="duty-template-add-btn"
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm">
          <Plus className="h-4 w-4" /> {editingId ? "Save changes" : "Create template"}
        </button>
      </div>
      <div className="md:col-span-2 p-5 rounded-lg border border-slate-800 bg-slate-900/60 md:order-1">
        <div className="font-heading text-lg font-semibold mb-3">Duty Templates</div>
        <div className="divide-y divide-slate-800">
          {templates.length === 0 && <div className="text-sm text-slate-500">No templates yet.</div>}
          {templates.map((t) => (
            <div key={t.id} className="py-3 flex items-center justify-between gap-3" data-testid={`duty-template-${t.id}`}>
              <div>
                <div className="text-sm font-medium">{t.name}</div>
                <div className="text-xs text-slate-500 font-mono-num mt-0.5">
                  {t.duty_start}–{t.duty_end}
                  {t.days?.length ? ` · ${t.days.length}-day pattern from ${t.anchor_date}` : ""}
                  {" · "}{roleName(t.role_id)} · {unitName(t.unit_id)}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <button onClick={() => edit(t)} data-testid={`duty-template-edit-${t.id}`}
                  className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1 transition-colors">
                  <Pencil className="h-3 w-3" /> Edit
                </button>
                <button onClick={() => del(t.id)} className="text-xs text-rose-400 hover:text-rose-300 inline-flex items-center gap-1">
                  <Trash2 className="h-3 w-3" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
